import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from '../components/Logo';

/**
 * A single, self-contained legal page that hosts both the Terms of Service and
 * the Privacy Policy. Reachable at /terms and /privacy — the `tab` prop (set per
 * route) picks which section is scrolled into view on load, and both are always
 * present on the page so cross-links and anchors work.
 *
 * This is a plain-language template, not legal advice — review with counsel
 * before relying on it. Update the CONTACT + effective date to your entity.
 */
const CONTACT_EMAIL = 'support@kaption.app';
const EFFECTIVE = 'September 20, 2026';

export function LegalPage({ tab = 'terms' }: { tab?: 'terms' | 'privacy' }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const id = pathname.includes('privacy') ? 'privacy' : pathname.includes('terms') ? 'terms' : tab;
    // Jump to the requested section without smooth-scroll jank on first paint.
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <div style={s.page}>
      <header style={s.top}>
        <div style={s.topIn}>
          <Link to="/welcome" aria-label="Kaption home" style={s.brand}>
            <Logo size={20} />
          </Link>
          <nav style={s.nav}>
            <a href="#terms" style={s.navLink}>Terms</a>
            <a href="#privacy" style={s.navLink}>Privacy</a>
            <Link to="/" style={s.navLink}>Back to app</Link>
          </nav>
        </div>
      </header>

      <main style={s.main}>
        <p style={s.eyebrow}>Last updated · {EFFECTIVE}</p>

        {/* ------------------------------- TERMS ------------------------------- */}
        <section id="terms" style={s.section}>
          <h1 style={s.h1}>Terms of Service</h1>
          <p style={s.p}>
            These Terms govern your access to and use of Kaption (the “Service”), a tool for
            transcribing, captioning, and editing short-form videos. By creating an account or using
            the Service, you agree to these Terms. If you do not agree, do not use the Service.
          </p>

          <h2 style={s.h2}>1. Your account</h2>
          <p style={s.p}>
            You must provide accurate information and are responsible for activity under your account
            and for keeping your credentials secure. You must be at least the age of majority in your
            jurisdiction, or have a parent or guardian’s consent, to use the Service.
          </p>

          <h2 style={s.h2}>2. Your content</h2>
          <p style={s.p}>
            You retain all ownership of the videos, audio, images, and text you upload or create
            (“Your Content”). You grant us a limited licence to store, process, and display Your
            Content solely to operate and improve the Service for you — for example to transcribe
            audio, generate captions, render exports, and produce previews. We do not sell Your
            Content. You are responsible for having the rights to everything you upload, and for
            ensuring it does not infringe others’ rights or break the law.
          </p>

          <h2 style={s.h2}>3. Acceptable use</h2>
          <p style={s.p}>
            You agree not to use the Service to upload unlawful, infringing, or harmful content; to
            reverse-engineer, overload, or disrupt the Service; to resell it without permission; or to
            attempt to access accounts or data that are not yours. We may suspend accounts that
            violate these Terms.
          </p>

          <h2 style={s.h2}>4. AI features</h2>
          <p style={s.p}>
            The Service uses automated and AI systems (transcription, keyword emphasis, the kMagic
            art-director, and similar) that can make mistakes. Automated output is provided “as is” for
            you to review and edit before publishing. You are responsible for the final result.
          </p>

          <h2 style={s.h2}>5. Plans, billing, and refunds</h2>
          <p style={s.p}>
            Paid plans are billed on a recurring basis through our payment processor, who may act as
            merchant of record and add applicable taxes (such as GST). Subscriptions renew
            automatically until cancelled; you can cancel at any time and retain access through the end
            of the current billing period. Prices and features may change with notice. Except where
            required by law, payments are non-refundable.
          </p>

          <h2 style={s.h2}>6. Third-party services</h2>
          <p style={s.p}>
            The Service integrates third parties — including payment processing, cloud storage, AI
            model providers, stock-media sources, and analytics — each governed by its own terms.
          </p>

          <h2 style={s.h2}>7. Termination</h2>
          <p style={s.p}>
            You may stop using the Service and delete your account at any time. We may suspend or
            terminate access if you breach these Terms or where required for security or legal reasons.
          </p>

          <h2 style={s.h2}>8. Disclaimers &amp; liability</h2>
          <p style={s.p}>
            The Service is provided “as is” without warranties of any kind. To the maximum extent
            permitted by law, we are not liable for indirect, incidental, or consequential damages, and
            our total liability is limited to the amount you paid us in the twelve months before the
            claim.
          </p>

          <h2 style={s.h2}>9. Changes</h2>
          <p style={s.p}>
            We may update these Terms; material changes will be posted here with a new “last updated”
            date. Continued use after changes means you accept them.
          </p>

          <h2 style={s.h2}>10. Contact</h2>
          <p style={s.p}>
            Questions about these Terms? Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.a}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <hr style={s.rule} />

        {/* ------------------------------ PRIVACY ------------------------------ */}
        <section id="privacy" style={s.section}>
          <h1 style={s.h1}>Privacy Policy</h1>
          <p style={s.p}>
            This policy explains what we collect, how we use it, and the choices you have when you use
            Kaption.
          </p>

          <h2 style={s.h2}>1. What we collect</h2>
          <p style={s.p}>
            <b>Account data</b> — your email and authentication details. <b>Content you upload</b> —
            videos, audio, and any captions, edits, or projects you create. <b>Usage data</b> — how you
            interact with the Service, device and browser information, and log data. <b>Billing data</b>{' '}
            — plan and transaction records; card details are handled by our payment processor, not
            stored by us.
          </p>

          <h2 style={s.h2}>2. How we use it</h2>
          <p style={s.p}>
            To provide the Service (transcribe, caption, render, and store your projects), to operate
            billing, to secure and improve the product, to provide support, and to comply with legal
            obligations. We process automated transcription and AI styling on the content you submit so
            we can return the edited result.
          </p>

          <h2 style={s.h2}>3. Analytics</h2>
          <p style={s.p}>
            We use Google Analytics to understand aggregate usage and improve the Service. It sets
            cookies and collects usage data as described in Google’s privacy policy. You can opt out
            using the Google Analytics opt-out browser add-on or your browser’s cookie controls.
          </p>

          <h2 style={s.h2}>4. Service providers</h2>
          <p style={s.p}>
            We share data only as needed with providers who help us run the Service — cloud hosting and
            storage, the payment processor (merchant of record), AI model providers used for
            transcription and editing, stock-media sources, and analytics. They may process data only
            on our behalf.
          </p>

          <h2 style={s.h2}>5. Retention &amp; deletion</h2>
          <p style={s.p}>
            We keep Your Content and account data while your account is active and as needed to provide
            the Service. You can delete projects at any time, and you can request account deletion by
            contacting us; we will remove your data except where we must retain records for legal,
            tax, or security reasons.
          </p>

          <h2 style={s.h2}>6. Security</h2>
          <p style={s.p}>
            We use reasonable technical and organisational measures to protect your data. No system is
            perfectly secure, so we cannot guarantee absolute security.
          </p>

          <h2 style={s.h2}>7. Your rights</h2>
          <p style={s.p}>
            Depending on where you live, you may have rights to access, correct, export, or delete your
            personal data, and to object to or restrict certain processing. To exercise these rights,
            contact us at the email below.
          </p>

          <h2 style={s.h2}>8. Children</h2>
          <p style={s.p}>
            The Service is not directed to children under 13 (or the minimum age in your jurisdiction),
            and we do not knowingly collect their personal data.
          </p>

          <h2 style={s.h2}>9. Changes</h2>
          <p style={s.p}>
            We may update this policy; material changes will be posted here with a new “last updated”
            date.
          </p>

          <h2 style={s.h2}>10. Contact</h2>
          <p style={s.p}>
            Privacy questions or requests? Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={s.a}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <footer style={s.foot}>
          <Link to="/welcome" style={s.footBrand}><Logo size={17} /></Link>
          <span style={s.footMark}>© {new Date().getFullYear()} Kaption</span>
        </footer>
      </main>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#0d0e12', color: '#c9ccd6', fontFamily: 'Inter, system-ui, sans-serif' },
  top: { position: 'sticky', top: 0, zIndex: 10, background: 'rgba(13,14,18,0.85)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #1c1e26' },
  topIn: { maxWidth: 820, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  brand: { display: 'inline-flex' },
  nav: { display: 'flex', gap: 18, alignItems: 'center' },
  navLink: { color: '#9aa0b0', fontSize: 14, textDecoration: 'none' },
  main: { maxWidth: 820, margin: '0 auto', padding: '40px 20px 64px' },
  eyebrow: { color: '#6f7484', fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' },
  section: { scrollMarginTop: 80 },
  h1: { color: '#f2f3f7', fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', margin: '18px 0 14px' },
  h2: { color: '#e7e8ee', fontSize: 17, fontWeight: 700, margin: '26px 0 8px' },
  p: { fontSize: 15, lineHeight: 1.75, color: '#b6bac6', margin: '0 0 12px' },
  a: { color: '#8aa0ff', textDecoration: 'none' },
  rule: { border: 'none', borderTop: '1px solid #1c1e26', margin: '44px 0' },
  foot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 56, paddingTop: 22, borderTop: '1px solid #1c1e26' },
  footBrand: { display: 'inline-flex' },
  footMark: { color: '#6f7484', fontSize: 13 },
};
